import {
  CreateFlowcodeFormContext,
  CreateFlowcodeFormContextType
} from './CreateFlowcodeFormContext'
import { CreateFlowcodeFormField, CreateFlowcodeStateActions } from './CreateFlowcodeForm.types'
import { FcShape } from '@dtx-company/flowcode-generator-browser/src'
import {
  FlowcodeTemplateType,
  GetFlowcodeTemplatesResponseType,
  LandingPageDestination,
  SocialMediaDestinations
} from '@dtx-company/inter-app/src/types/flowcode'
import { GET_FLOWCODE_TEMPLATES_QUERY } from '../../../gql/queries/flowcode'
import {
  SQUARE_CODE_PATTERN_CONFIGS,
  findMatchedPattern
} from '../../../components/Homepage/CreateFlowcodeForm/CreateFlowcodeFormDesktop/ManualDesignStep/ManualDesignStep.constants'
import { getIsFlowcodeInvertible } from '@app/common/src/utils/flowcode'
import {
  setBorder,
  setBorderShape,
  setColor,
  setDestination,
  setDownloadFileType,
  setLoadingCreateFlowcode,
  setRotate,
  setShortURL,
  setTemplate,
  setTheme,
  toggleTransparency
} from '../../../redux/slices/createFlowcodeSlice'
import { useCodeSelector } from '@dtx-company/inter-app/src/hooks/useCodeSelector'
import { useContext, useEffect, useMemo, useState } from 'react'
import { useCreateFlowcodeState } from '../../../hooks/useCreateFlowcodeState'
import { useDispatch } from 'react-redux'
import events from '@dtx-company/inter-app/src/event-tracking/events/flowcode'
import useSWR from 'swr'

export function useCenterImageFile(): File | null {
  const logoImageUrl = useCodeSelector(
    state => state.homePage.autoDesignedCodeOptions?.logoImageUrl
  )
  const [centerImageFile, setCenterImageFile] = useState<File | null>(null)
  useEffect(() => {
    if (logoImageUrl)
      fetch(logoImageUrl)
        .then(result => result.blob())
        .then(blob => setCenterImageFile(new File([blob], 'centerImageFile')))
  }, [logoImageUrl])
  return centerImageFile
}

/**
 * Fetches available flowcode templates and sets an initial one
 * @returns list of flowcode templates
 */
export const useFlowcodeTemplates = (
  allowSetBorderShape = true
): {
  isLoading: boolean
  templates: FlowcodeTemplateType[]
  error?: string
  hasTemplates: boolean
} => {
  const { data, error } = useSWR<GetFlowcodeTemplatesResponseType>(GET_FLOWCODE_TEMPLATES_QUERY, {
    revalidateOnMount: false,
    revalidateOnFocus: false
  })
  const dispatch = useDispatch()
  const isLoading = Boolean(!data && !error)

  const { borderShape } = useCreateFlowcodeState()

  const templates: FlowcodeTemplateType[] = useMemo(
    () =>
      data?.flowcodeTemplates?.map(template => {
        const options: FlowcodeTemplateType['options'] = template?.configuration
          ? JSON.parse(template?.configuration)
          : undefined
        return {
          ...template,
          isInvertible: getIsFlowcodeInvertible(template),
          options
        }
      }) ?? [],
    [data?.flowcodeTemplates]
  )

  const { template: defaultTemplate } = useCreateFlowcodeState()

  useEffect(() => {
    if (!defaultTemplate && !isLoading && templates.length) {
      dispatch(setTemplate(templates[0]))
    }
  }, [defaultTemplate, templates, dispatch, isLoading])
  const { template } = useCreateFlowcodeState()

  useEffect(() => {
    if (allowSetBorderShape) {
      if (borderShape === FcShape.SQUARE) {
        const matchedTemplate = findMatchedPattern(SQUARE_CODE_PATTERN_CONFIGS, template)
        dispatch(setTemplate(matchedTemplate || SQUARE_CODE_PATTERN_CONFIGS[0]))
      } else if (templates && templates.length > 0) {
        const matchedTemplate = findMatchedPattern(templates, template)
        dispatch(setTemplate(matchedTemplate || templates[0]))
      }
    }
  }, [borderShape, dispatch, templates, allowSetBorderShape, template])

  return {
    isLoading,
    templates: borderShape === FcShape.SQUARE ? SQUARE_CODE_PATTERN_CONFIGS : templates,
    error,
    hasTemplates: Boolean(templates?.length && !error)
  }
}

/**
 *
 * Returns actions to update state related to creating a flowcode.
 * Heap events are also dispatched.
 *
 */
export const useCreateFlowcodeStateActions = (arg?: {
  variant?: string | null
}): CreateFlowcodeStateActions => {
  const dispatch = useDispatch()
  return {
    setTemplate: template => {
      dispatch(setTemplate(template))
      if (template?.assetFile?.assetName) {
        events.userSelectedFlowcodeTemplate(template?.assetFile?.assetName, arg?.variant)
      }
    },
    setColor: color => {
      dispatch(setColor(color))
      events.userChoseFlowcodeColor(color.label, undefined, undefined, arg?.variant)
    },
    setTheme: theme => {
      dispatch(setTheme(theme))
      events.userSelectedFlowcodeTheme(theme.label)
    },
    setDownloadFileType: fileType => {
      dispatch(setDownloadFileType(fileType))
    },
    toggleTransparency: transparent => {
      dispatch(toggleTransparency(transparent))
    },
    setDestination: destination => {
      events.userSelectedFlowcodeDestination(destination.label, destination.id)
      dispatch(setDestination(destination))
    },
    setLoadingCreateFlowcode: loading => {
      dispatch(setLoadingCreateFlowcode(loading))
    },
    setBorder: border => {
      dispatch(setBorder(border))
      if (border?.assetFile?.assetName) {
        events.userSelectedFlowcodeBorder(border.assetFile.assetName)
      }
    },
    setShortURL: url => {
      dispatch(setShortURL(url))
    },
    setBorderShape: shape => {
      dispatch(setBorderShape(shape))
    },
    setRotate: rotate => {
      dispatch(setRotate(rotate))
    }
  }
}

/**
 * Returns create flowcode form context
 */
export const useCreateFlowcodeForm = (): CreateFlowcodeFormContextType => {
  const context = useContext(CreateFlowcodeFormContext)
  if (!context) {
    throw new Error('useCreateFlowcodeForm must be used within a CreateFlowcodeFormProvider')
  }

  return context
}

const isSocialDestination = (destination: string): boolean =>
  destination === LandingPageDestination.SOCIAL_MEDIA || destination in SocialMediaDestinations

// TEMP: need to match the way mobile passes social media ids
export const useSocialMediaId = ({
  socialMediaId,
  destination
}: {
  socialMediaId: LandingPageDestination
  destination: LandingPageDestination
}): void => {
  const { setValue, watch } = useCreateFlowcodeForm()
  const subtypeId = watch('subtypeId')

  useEffect(() => {
    if (isSocialDestination(destination) && (!subtypeId || subtypeId !== socialMediaId)) {
      setValue(CreateFlowcodeFormField.SocialMediaId, socialMediaId)
    } else if (!isSocialDestination(destination) && subtypeId) {
      setValue(CreateFlowcodeFormField.SocialMediaId, '')
    }
  }, [destination, socialMediaId, setValue, subtypeId])
}
